import {
  BackupPolicyType,
  ScheduleMode,
  type BackupPolicyConfig,
} from '@repo/api-gw-sdk';

import { Loader } from '@/components/layout/loading';
import { getResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import type { VaultManagedType } from '@/components/vaults/vaultManagedTypeSelect';
import { Wizard } from '@/components/wizard/Wizard';
import { useDAL } from '@/contexts/useDAL';
import {
  createAllIncludedCondition,
  createAllExcludedCondition,
} from '@/types/advanceFilter';

import {
  EditBackupPolicyFlow,
  type BackupPolicyEditState,
} from './BackupPolicyCreationFlow';
import { defaultBackupPolicy } from './defaultBackupPolicy';

interface BackupPolicyPanelProps {
  entity?: BackupPolicyConfig;
  onSave: (policy: BackupPolicyConfig) => Promise<void>;
  onAbort: () => void;
}

export default function BackupPolicyPanel(props: BackupPolicyPanelProps) {
  const skipFirstStep = !!props.entity;
  const creatingNew = !props.entity;
  const backupPolicyConfig = props.entity || { ...defaultBackupPolicy };
  const dal = useDAL();
  const { data: vaultAccounts, isLoading } = dal.cloudAccounts.vault.list();
  const eonManagedVaultAccounts =
    vaultAccounts?.accounts.filter((account) => !account.isCustomerOwned) || [];
  const selfManagedVaultAccounts =
    vaultAccounts?.accounts.filter((account) => account.isCustomerOwned) || [];
  const vaultAccount = vaultAccounts?.accounts.find(
    (account) => account.id === backupPolicyConfig.schedules[0]?.vaultId
  );
  let vaultManagedType: VaultManagedType = 'eon-managed';
  if (vaultAccount) {
    vaultManagedType = vaultAccount.isCustomerOwned
      ? `self-managed-${vaultAccount.cloudProvider}`
      : 'eon-managed';
  } else if (eonManagedVaultAccounts.length) {
    vaultManagedType = 'eon-managed';
  } else if (selfManagedVaultAccounts.length) {
    vaultManagedType = `self-managed-${selfManagedVaultAccounts[0].cloudProvider}`;
  }

  const save = async (state: BackupPolicyEditState) => {
    const condition =
      state.resourcesScopeType === 'allIncluded'
        ? createAllIncludedCondition()
        : state.resourcesScopeType === 'allExcluded'
          ? createAllExcludedCondition()
          : state.condition;

    if (!condition) {
      return false;
    }

    await props.onSave({
      ...backupPolicyConfig,
      name: state.name,
      condition: condition,
      schedules: state.schedules ?? [],
      include: state.include ?? [],
      exclude: state.exclude ?? [],
      scheduleMode: state.mode,
      policyType: state.policyType,
      resourceTypes: state.resourceTypes,
    });

    return true;
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Wizard<BackupPolicyEditState>
      onAbort={props.onAbort}
      hideBackArrow={true}
      onFinish={save}
      initialStep={{ stepIndex: skipFirstStep ? 1 : 0 }}
      flow={[EditBackupPolicyFlow]}
      initialState={{
        id: backupPolicyConfig.id,
        name: backupPolicyConfig.name,
        focusOnName: skipFirstStep && !backupPolicyConfig.id,
        condition: backupPolicyConfig.condition,
        schedules: backupPolicyConfig.schedules,
        include: backupPolicyConfig.include || [],
        exclude: backupPolicyConfig.exclude || [],
        resourcesScopeType: getResourcesScopeType(
          backupPolicyConfig.condition,
          'useConditions'
        ),
        mode:
          !backupPolicyConfig.scheduleMode ||
          backupPolicyConfig.scheduleMode ===
            ScheduleMode.SCHEDULE_MODE_UNSPECIFIED
            ? ScheduleMode.SCHEDULE_MODE_BASIC
            : backupPolicyConfig.scheduleMode,
        policyType:
          backupPolicyConfig.policyType ||
          BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD,
        resourceTypes: backupPolicyConfig.resourceTypes,
        vaultManagedType,
      }}
      title={
        backupPolicyConfig.id
          ? 'Edit backup policy'
          : creatingNew
            ? 'Create backup policy'
            : 'Duplicate backup policy'
      }
      stepperLabels={[
        'Resource selection',
        'Resource list',
        'Backup schedules',
      ]}
    />
  );
}
